import { useEffect, useState } from 'react';
import { verAjustes } from '../api/triviais';
import { Selo } from '../ui';

/**
 * O alerta da cápsula esquecida na estufa (onda 305, RN-250).
 *
 * «Estufa: acredito que o ideal é que as cápsulas pendentes fiquem lá
 * aguardando o valor, independente da quantidade de horas. Mas ao se
 * passar 24 horas cria-se um alerta» — Humberto, 08/10/2026, que escolheu
 * vê-lo na Estufa, na ficha e no Painel.
 *
 * O limiar é da Testesolo e mora nos Ajustes do sistema
 * (`alerta_estufa_horas`, 24 h por padrão). A Estufa e as fichas da 294
 * recebem o número junto com as linhas; quem não recebe (a Pesagem seca dos
 * triviais) lê dos ajustes uma vez por sessão. A conta é feita na tela, com
 * o relógio acertado pelo servidor (`useRelogio`), para que a cápsula que
 * passa do limiar com a tela aberta mude de cara sem recarregar.
 *
 * O alerta NÃO bloqueia nada — a pesagem seca é aceita a qualquer hora —
 * e diz em palavra, não só em cor (§18): «há mais de 24 h».
 */

const PADRAO = 24;
let doAjuste: number | null = null;
let pedido: Promise<number> | null = null;

function lerDosAjustes(): Promise<number> {
  pedido ??= verAjustes()
    .then((lista) => {
      const a = lista.find((x: any) => x.chave === 'alerta_estufa_horas');
      doAjuste = a ? Number(a.valor) : PADRAO;
      return doAjuste;
    })
    .catch(() => { pedido = null; return PADRAO; });
  return pedido;
}

/**
 * O limiar em horas. `doServidor` é o que a rota já trouxe (a Estufa e a
 * ficha); sem ele, lê dos ajustes — e até lá vale o padrão.
 */
export function useAlertaDaEstufa(doServidor?: number | null): number {
  const [h, setH] = useState<number>(doAjuste ?? PADRAO);
  useEffect(() => {
    if (doServidor != null) { doAjuste = Number(doServidor); return; }
    let vivo = true;
    void lerDosAjustes().then((v) => { if (vivo) setH(v); });
    return () => { vivo = false; };
  }, [doServidor]);
  return doServidor != null && Number.isFinite(Number(doServidor)) ? Number(doServidor) : h;
}

/** A cápsula que entrou na estufa em `desde` já passou do limiar? */
export function passouDoAlerta(desde: unknown, agora: number, horas: number): boolean {
  if (!desde) return false;
  const a = Date.parse(String(desde));
  if (!Number.isFinite(a)) return false;
  return agora - a >= horas * 3600e3;
}

/** «24 h», «1,5 h» — o limiar como a bancada lê. */
export const horasDoAlerta = (horas: number) =>
  `${horas.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`;

/** O selo «há mais de 24 h» — só aparece quando a cápsula passou do limiar. */
export function SeloDaEstufa({ desde, agora, horas }: {
  desde: unknown; agora: number; horas: number;
}) {
  if (!passouDoAlerta(desde, agora, horas)) return null;
  return (
    <span className="ne-alerta-selo">
      <Selo tom="critico" titulo="A cápsula continua na estufa; a pesagem seca é aceita a qualquer hora (RN-250).">
        Há mais de {horasDoAlerta(horas)}
      </Selo>
    </span>
  );
}
